import React, { useState, useRef, useMemo } from "react"
import PropTypes from "prop-types"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Progress } from "reactstrap"
import { descargarExportGlobal } from "./api"

const BotonExportGlobal = ({ nitemisor, prefijos, fechaDesde, fechaHasta }) => {
  const [modalConfirm, setModalConfirm] = useState(false)
  const [exportando, setExportando] = useState(false)
  const [progreso, setProgreso] = useState({ hechas: 0, total: 0 })
  const [resultado, setResultado] = useState(null)
  const [error, setError] = useState("")
  const cancelRef = useRef(false)

  const totalPrefijosConGaps = useMemo(() => {
    if (!prefijos) return 0
    return prefijos.filter(p => (p.gaps_estimados ?? 0) > 0).length
  }, [prefijos])

  const abrirModal = () => {
    setResultado(null)
    setError("")
    setProgreso({ hechas: 0, total: 0 })
    setModalConfirm(true)
  }

  const cerrarModal = () => {
    if (exportando) {
      cancelRef.current = true
      return
    }
    setModalConfirm(false)
  }

  const comenzar = async () => {
    cancelRef.current = false
    setExportando(true)
    setError("")
    setResultado(null)
    setProgreso({ hechas: 0, total: totalPrefijosConGaps })

    try {
      const res = await descargarExportGlobal({
        nitemisor,
        prefijos,
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        onProgress: (hechas, total) => setProgreso({ hechas, total }),
        isCancelled: () => cancelRef.current,
      })
      setResultado(res)
    } catch (err) {
      setError(err.message || "Error al exportar.")
    } finally {
      setExportando(false)
    }
  }

  const porcentaje = progreso.total > 0 ? Math.round((progreso.hechas / progreso.total) * 100) : 0

  const deshabilitado = totalPrefijosConGaps === 0

  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-outline-success"
        onClick={abrirModal}
        disabled={deshabilitado}
        title={
          deshabilitado
            ? "No hay prefijos con gaps"
            : `Exportar ${totalPrefijosConGaps} prefijos a un CSV consolidado`
        }
      >
        <i className="mdi mdi-database-export-outline me-1" />
        Exportar todo ({totalPrefijosConGaps})
      </button>

      <Modal isOpen={modalConfirm} toggle={cerrarModal} backdrop={exportando ? "static" : true}>
        <ModalHeader toggle={exportando ? null : cerrarModal}>
          {resultado ? "Exportación completada" : exportando ? "Exportando…" : "Exportar todos los gaps"}
        </ModalHeader>
        <ModalBody>
          {!exportando && !resultado && !error && (
            <>
              <p>
                Se descargará un CSV consolidado con los gaps de{" "}
                <strong>{totalPrefijosConGaps}</strong> prefijo{totalPrefijosConGaps !== 1 ? "s" : ""}
                {" "}con gaps.
              </p>
              <div
                style={{
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: 8,
                  padding: "10px 14px",
                  fontSize: 13,
                  color: "#92400e",
                }}
              >
                <i className="mdi mdi-information-outline me-1" />
                Este proceso puede tardar varios minutos si hay muchos prefijos. Se realizan 4
                descargas en paralelo al backend.
              </div>
            </>
          )}

          {(exportando || resultado) && !error && (
            <>
              <div className="mb-2 d-flex justify-content-between">
                <span style={{ fontSize: 13, color: "#475569" }}>
                  {resultado ? "Completado" : "Descargando CSVs…"}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {progreso.hechas} / {progreso.total}
                </span>
              </div>
              <Progress
                value={porcentaje}
                color={resultado ? "success" : "primary"}
                animated={exportando}
                striped={exportando}
                style={{ height: 10 }}
              >
                {porcentaje}%
              </Progress>
              {resultado && (
                <div className="mt-3">
                  <div
                    style={{
                      background: "#ecfdf5",
                      border: "1px solid #a7f3d0",
                      borderRadius: 8,
                      padding: "10px 14px",
                      fontSize: 13,
                      color: "#065f46",
                    }}
                  >
                    <i className="mdi mdi-check-circle me-1" />
                    Archivo descargado: <strong>{resultado.totalLineas.toLocaleString("es-CO")}</strong>{" "}
                    líneas de {resultado.totalPrefijos} prefijos.
                  </div>
                  {resultado.errores && resultado.errores.length > 0 && (
                    <div
                      className="mt-2"
                      style={{
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        borderRadius: 8,
                        padding: "10px 14px",
                        fontSize: 12,
                        color: "#991b1b",
                      }}
                    >
                      <i className="mdi mdi-alert-circle me-1" />
                      {resultado.errores.length} prefijo
                      {resultado.errores.length !== 1 ? "s fallaron" : " falló"}:{" "}
                      {resultado.errores
                        .slice(0, 5)
                        .map(e => e.prefijo)
                        .join(", ")}
                      {resultado.errores.length > 5 &&
                        ` y ${resultado.errores.length - 5} más`}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {error && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 8,
                padding: "10px 14px",
                fontSize: 13,
                color: "#991b1b",
              }}
            >
              <i className="mdi mdi-alert-circle me-1" />
              {error}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {!exportando && !resultado && !error && (
            <>
              <Button color="secondary" onClick={cerrarModal}>Cancelar</Button>
              <Button color="primary" onClick={comenzar}>
                <i className="mdi mdi-download me-1" />
                Comenzar descarga
              </Button>
            </>
          )}
          {exportando && (
            <Button color="danger" outline onClick={() => (cancelRef.current = true)}>
              Cancelar
            </Button>
          )}
          {(resultado || error) && (
            <Button color="primary" onClick={() => setModalConfirm(false)}>
              Cerrar
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </>
  )
}

BotonExportGlobal.propTypes = {
  nitemisor: PropTypes.string.isRequired,
  prefijos: PropTypes.array,
  fechaDesde: PropTypes.string,
  fechaHasta: PropTypes.string,
}

export default BotonExportGlobal
